import { useCallback, useEffect, useRef, useState } from "react";
import { AUTH } from "../design/copy.js";
import { isAddressIncomplete, isEmailAlreadyVerified, isEmailMismatch, isTooManyTries } from "./auth-errors.js";

/**
 * How long a sent link's door rests before it can be pressed again. The api
 * sends one of each kind a minute to an account at most and holds back the
 * rest without saying so; a door pressed again inside that minute would
 * claim a link that never left. Five seconds past the minute (the
 * pre-release review, 2026-10-22): the rest starts when the answer reaches
 * the browser, the api's minute only at the first link's ledger row, which
 * is written once Mailjet has answered.
 */
export const RESEND_REST_MS = 65_000;

/**
 * What a press came to: a link on its way (`sent`), a request that was not
 * answered (`notSent`), either limit (`tooMany`), and better-auth's two
 * refusals of a signed-in browser's resend (review, 2026-10-22): the
 * session's address is already confirmed (`alreadyConfirmed`, 400
 * EMAIL_ALREADY_VERIFIED), or the session belongs to a different address
 * (`otherAccount`, 400 EMAIL_MISMATCH). An address better-auth will not take
 * (`addressIncomplete`, its 400 VALIDATION_ERROR) since the pre-release
 * review, 2026-10-22.
 */
export type AccountSaid = "sent" | "notSent" | "tooMany" | "alreadyConfirmed" | "otherAccount" | "addressIncomplete";

/**
 * Whether the keyboard has lost its place (the pre-release review, 2026-10-22):
 * a door that goes to rest takes its pressed button away, and focus falls to
 * the page. Its page then moves focus to the sentence that says what the
 * press came to. Focus anywhere else (the address field, after Enter) stays.
 */
export function focusDropped(): boolean {
  const active = globalThis.document.activeElement;
  return active === null || active === globalThis.document.body;
}

/** A refused or unanswered press, in the card's words (the verify page's resend says them too). */
export function accountRefusalWords(said: Exclude<AccountSaid, "sent">): string {
  switch (said) {
    case "tooMany":
      return AUTH.tooManyTries;
    case "alreadyConfirmed":
      return AUTH.alreadyConfirmed;
    case "otherAccount":
      return AUTH.otherAccount;
    case "addressIncomplete":
      return AUTH.addressIncomplete;
    case "notSent":
      return AUTH.notSent;
  }
}

function saidOf(error: unknown): Exclude<AccountSaid, "sent"> {
  if (isTooManyTries(error)) return "tooMany";
  if (isEmailAlreadyVerified(error)) return "alreadyConfirmed";
  if (isEmailMismatch(error)) return "otherAccount";
  if (isAddressIncomplete(error)) return "addressIncomplete";
  return "notSent";
}

/**
 * One account-email door: a press sends once (a second press before the
 * answer sends nothing), an answered request rests the door for
 * RESEND_REST_MS, and a request that was not answered may be tried again at
 * once. What was said stays until the next press.
 *
 * `rest()` (review, 2026-10-22) starts the same rest with nothing said, for a
 * door that opens just after another request sent its kind of link (an email
 * sign-up, a sign-in refused for an unconfirmed address): pressed inside that
 * minute it would say a link is on its way while the api held it back.
 *
 * `restingOnMount` (the pre-release review, 2026-10-22) opens the door
 * already resting, for a card handed back the check-your-email state: the
 * first render hides the door, and the mount effect that owns the rest's
 * timer starts it and clears it, so StrictMode's second run of that effect
 * starts it again rather than leaving the door resting for good.
 */
export function useAccountSend(options: { readonly restingOnMount?: boolean } = {}): {
  readonly phase: "ready" | "sending" | "resting";
  readonly said: AccountSaid | null;
  readonly send: (request: () => Promise<unknown>) => Promise<void>;
  readonly rest: () => void;
  readonly reset: () => void;
} {
  /** Read once: what the door opened as. */
  const [restingOnMount] = useState(options.restingOnMount === true);
  const [phase, setPhase] = useState<"ready" | "sending" | "resting">(restingOnMount ? "resting" : "ready");
  const [said, setSaid] = useState<AccountSaid | null>(null);
  const busy = useRef(restingOnMount);
  const restTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRest = (): void => {
    if (restTimer.current !== null) clearTimeout(restTimer.current);
    restTimer.current = null;
  };
  const startRest = useCallback((): void => {
    clearRest();
    busy.current = true;
    setPhase("resting");
    restTimer.current = setTimeout(() => {
      restTimer.current = null;
      busy.current = false;
      setPhase("ready");
    }, RESEND_REST_MS);
  }, []);
  useEffect(() => {
    if (restingOnMount) startRest();
    return clearRest;
  }, [restingOnMount, startRest]);
  const send = useCallback(
    async (request: () => Promise<unknown>): Promise<void> => {
      if (busy.current) return;
      busy.current = true;
      setSaid(null);
      setPhase("sending");
      try {
        await request();
        setSaid("sent");
        startRest();
      } catch (error) {
        busy.current = false;
        setSaid(saidOf(error));
        setPhase("ready");
      }
    },
    [startRest],
  );
  const rest = useCallback((): void => {
    setSaid(null);
    startRest();
  }, [startRest]);
  const reset = useCallback((): void => {
    clearRest();
    busy.current = false;
    setSaid(null);
    setPhase("ready");
  }, []);
  return { phase, said, send, rest, reset };
}
